/**
 * Asserts that any condition is true, otherwise throwing an error message
 * which can be configured through the message parameter. Optionally, you can
 * also log something before the error is thrown through the log parameter.
 *
 * See: https://www.typescriptlang.org/docs/handbook/release-notes/typescript-3-7.html#assertion-functions
 */
export function S25Assert(condition: any, message: string, log?: () => void): asserts condition {
    if (!condition) {
        log?.();
        throw new Error(message);
    }
}

export namespace S25Assert {
    /**
     * This assertion will throw an error if value is undefined or null. The error
     * message is configurable via parameter msg
     *
     * See: https://www.typescriptlang.org/docs/handbook/release-notes/typescript-3-7.html#assertion-functions
     */
    export function isDefined<T>(value: T, msg?: string): asserts value is NonNullable<T> {
        S25Assert(
            value !== undefined && value !== null,
            msg ?? `Expected value to be defined, but got ${typeof value === "undefined" ? "undefined" : "null"}`,
        );
    }

    /**
     * Convenience wrapper for isDefined that returns the value
     */
    export function definedValue<T>(value: T, msg?: string): NonNullable<T> {
        isDefined(value, msg);
        return value;
    }

    /**
     * Use this to close out a switch statement or if/else statement that should be exhaustive.
     * Then, if somebody adds a new type to your union, the compiler will error!
     * e.g.
     * ```ts
     * type Shape = "circle" | "square";
     * function getArea(shape: Shape) {
     *   switch(shape) {
     *     case "circle": return Math.PI * r * r;
     *     case "square": return s * s;
     *     default: S25Assert.assertNever(shape);
     *   }
     * }
     * ```
     *
     * Thanks Ryan Cavanaugh! https://stackoverflow.com/a/39419171
     */
    export function never(neverType: never, message: string = "Unexpected value provided"): never {
        throw new Error(message + ": " + String(neverType));
    }
}
